import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { fetchLockedBalances } from "@/services/real/lock.service";
import { QUERY_KEYS } from "@/config";
import { formatEther, type Address } from "viem";
import { useProtocolPNL } from "./useWalletAPYStore";

export interface LockedPositionStore {
    lockDuration: string;
    lockedBalance: string;
    rewardsEarned: string;
    liUSDBalance: string;
    iUSDBalance: string;
}

interface UseLockPositionsReturn {
    lockedPositions: LockedPositionStore[];
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
    isSuccess: boolean;
    isPending: boolean;
    totalLocked: number;
    totalRewards: number;
}

export const useLockPositions = (): UseLockPositionsReturn => {
    const { address, isConnected } = useAccount();
    const queryKey = useMemo(() => QUERY_KEYS.POSITIONS.LOCK.LIST(address), [address]);
    const queryFn = useCallback(async () => {
        if (!isConnected || !address) {
            throw new Error("Wallet not connected");
        }
        return await fetchLockedBalances(address as Address);
    }, [address, isConnected]);

    const { data: pnlData } = useProtocolPNL(address);

    const {
        data: positions,
        isLoading,
        isError,
        error,
        isSuccess,
        isPending,
    } = useQuery({
        queryKey,
        queryFn,
        enabled: isConnected && !!address,
    });

    const { lockedPositions, totalLocked, totalRewards } = useMemo(() => {
        const lockedPositions =
            positions?.map((position) => ({
                lockDuration: position.lockDuration,
                lockedBalance: position.lockedBalance,
                rewardsEarned: `${pnlData?.lockedProfitNormalized[position.lockDuration.split("w")[0]]}` ?? "0",
                liUSDBalance: position.liUSDBalance,
                iUSDBalance: position.iUSDBalance,
            })) ?? [];

        const totalLocked = lockedPositions.reduce(
            (acc, position) => acc + Number(formatEther(BigInt(position.iUSDBalance))),
            0,
        );
        const totalRewards = lockedPositions.reduce((acc, position) => acc + Number(position.rewardsEarned), 0);

        return {
            lockedPositions,
            totalLocked,
            totalRewards,
        };
    }, [positions, pnlData]);

    return {
        lockedPositions,
        isLoading,
        isError,
        error,
        isSuccess,
        isPending,
        totalLocked,
        totalRewards,
    };
};
